import type { SolutionDomain } from '@energiebespaarders/constants';
import { SOLUTION_DOMAINS_NL } from '@energiebespaarders/constants';
import { Box, Button, Flex, Image } from '@energiebespaarders/symbols';
import { ChatBubbles } from '@energiebespaarders/symbols/icons/solid';
import React, { useMemo } from 'react';
import { useIntercom } from 'react-use-intercom';
import { margin } from 'src/styles/mixins';
import styled from 'styled-components';
import HybridLink from '~/components/HybridLink';
import KlantenVertellenWidget from '~/components/KlantenVertellenWidget';
import useMediaQuery from '~/hooks/useMediaQuery';
import SocialLinks from '../SocialLinks';
import Logo from '/public/img/logo_WEB.svg';

const StyledFooter = styled.footer`
  position: relative;
  border-top: 1px solid ${x => x.theme.colors.grayLight};
  text-align: center;
  font-size: ${x => x.theme.type.scale[6]};
  overflow: hidden;
`;

const StyledSectionHeading = styled.div`
  ${margin(1, 'bottom')};
  font-size: ${x => x.theme.type.scale[6]};
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.025em;
  color: ${x => x.theme.colors.purple};
`;

const StyledFooterLink = styled(HybridLink)`
  ${margin(0.5, 'y')};
  display: inline-block;
  line-height: 1.5;
  color: ${x => x.theme.colors.grayDarker};
`;

const StyledSubFooter = styled(Flex)`
  position: absolute;
  bottom: 0;
  width: 100%;
  background: ${x => x.theme.colors.grayLighter};
  color: ${x => x.theme.colors.grayDarker};
  border: 1px solid ${x => x.theme.colors.grayLight};
  font-size: ${x => x.theme.type.scale[7]};
`;

const StyledSubFooterLink = styled(HybridLink)`
  color: ${x => x.theme.colors.grayDarker};
`;

export const Footer: React.FC = () => {
  const intercom = useIntercom();
  const isTablet = useMediaQuery('lg');

  const solutionDomainLinks = useMemo(
    () =>
      [
        'pvSystem',
        'wallInsulation',
        'floorInsulation',
        'roofInsulation',
        'insulationGlazing',
      ] as SolutionDomain[],
    [],
  );
  const moreInfoLinks = useMemo(
    () => [
      { to: '/over-ons', title: 'Lees meer over Energiebespaarders', label: 'Over ons' },
      {
        to: '/klanten',
        title: 'Lees over de ervaringen van onze klanten',
        label: 'Onze klanten vertellen',
      },
      { to: '/faq', title: 'Vind hier een antwoord op jouw vragen', label: 'Veelgestelde vragen' },
      {
        to: '/blog',
        title: 'Het laatste nieuws over duurzaam wonen, subsidies en handige bespaartips',
        label: 'Onze blog',
      },
    ],
    [],
  );

  const columns = 4;

  return (
    <StyledFooter>
      <Flex flexWrap="wrap" mx={-2} pt={8} px={2} pb={['90px', '60px', '60px', 0]}>
        {!isTablet && (
          <Box width={1 / 5} px={2}>
            <KlantenVertellenWidget />
          </Box>
        )}
        <Box width={[1, 1 / 2, 1 / (columns - 1), 1 / columns]} px={2} mb={3}>
          <StyledSectionHeading>Oplossingen</StyledSectionHeading>
          {solutionDomainLinks.map((sol, index) => (
            <div key={`${sol}-${index}`}>
              <StyledFooterLink
                to={`/woning-verduurzamen/product/${SOLUTION_DOMAINS_NL[sol].toLowerCase()}`}
                title={`Lees meer over ${SOLUTION_DOMAINS_NL[sol].toLowerCase()}`}
                prefetch={false}
              >
                {SOLUTION_DOMAINS_NL[sol]}
              </StyledFooterLink>
            </div>
          ))}
          <div>
            <StyledFooterLink
              to="/woning-verduurzamen/product/warmtepomp"
              prefetch={false}
              title="Lees meer over warmtepompen"
            >
              Warmtepomp
            </StyledFooterLink>
          </div>
          <div>
            <StyledFooterLink
              to="/woning-verduurzamen/product/airco"
              prefetch={false}
              title="Lees meer over airco's"
            >
              Airco
            </StyledFooterLink>
          </div>
          <div>
            <StyledFooterLink
              to="/woning-verduurzamen/product/cv-ketel"
              prefetch={false}
              title="Lees meer over cv-ketels"
            >
              CV-ketel
            </StyledFooterLink>
          </div>
        </Box>
        <Box width={[1, 1 / 2, 1 / (columns - 1), 1 / columns]} px={2} mb={3}>
          <StyledSectionHeading>Meer weten</StyledSectionHeading>
          {moreInfoLinks.map((link, index) => {
            const linkProps = {
              to: link.to,
              prefetch: false,
            };
            return (
              <div key={`${link.to}-${index}`}>
                <StyledFooterLink title={link.title} {...linkProps}>
                  {link.label}
                </StyledFooterLink>
              </div>
            );
          })}
        </Box>
        <Box width={[1, 1 / 2, 1 / (columns - 1), 1 / columns]} px={2}>
          <StyledSectionHeading>Contact</StyledSectionHeading>
          <div>
            <StyledFooterLink
              href="https://g.page/energiebespaarders?share"
              className="link-gray image-link"
              title="Bekijk onze locatie op Google Maps"
              rel="noopener noreferrer"
              target="_blank"
            >
              Reguliersdwarsstraat 58A
              <br />
              1017BM Amsterdam
            </StyledFooterLink>
          </div>
          <div>
            <Button
              iconStart={ChatBubbles}
              inverse
              label="Neem contact op"
              bgColor="grayDarker"
              onClick={() => intercom.showNewMessage()}
              mt={2}
              mr={0}
              style={{ borderRadius: 3 }}
            />
          </div>

          <SocialLinks />
        </Box>
        <Box width={1} my={[5, 5, 5, 0]}>
          <HybridLink to="/" prefetch={false}>
            <Image src={Logo} alt="Energiebespaarders" width={isTablet ? '180px' : '180px'} />
          </HybridLink>
        </Box>
        {isTablet && (
          <Box width={1} px={2}>
            <KlantenVertellenWidget />
          </Box>
        )}
      </Flex>

      <StyledSubFooter py={1}>
        <Box width={1}>
          <StyledSubFooterLink to="/privacy" prefetch={false}>
            Privacy statement
          </StyledSubFooterLink>
          &nbsp; &bull; &nbsp;
          <StyledSubFooterLink to="/cookies" prefetch={false}>
            Cookiebeleid
          </StyledSubFooterLink>
          <br />
          {`Energiebespaarders © ${new Date().getFullYear()}. Alle rechten voorbehouden.`}
        </Box>
      </StyledSubFooter>
    </StyledFooter>
  );
};

export default Footer;
